import { useEffect, useState, type FormEvent, type ReactElement } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Heart, Sparkles, Inbox, ShoppingBag } from 'lucide-react'
import { api } from '@/services/api'
import { useAuth } from '@/lib/auth'
import {
  formatPrice,
  formatDate,
  STATUS_LABELS_FR,
  SERVICE_CATEGORY_LABELS,
  PRODUCT_CATEGORY_LABELS,
  statusBadgeVariant,
} from '@/lib/format'
import type { Service, Product, Request as Req, Quote, Order, Payment } from '@/lib/types'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Select } from '@/components/ui/select'

// =============================================================================
// Page header helper
// =============================================================================
function PageHeader({
  title,
  eyebrow,
  action,
}: {
  title: string
  eyebrow?: string
  action?: ReactElement
}): ReactElement {
  return (
    <div className="mb-8 flex items-end justify-between">
      <div>
        {eyebrow && <p className="text-xs uppercase tracking-[0.3em] text-accent">{eyebrow}</p>}
        <h1 className="font-display mt-1 text-4xl text-primary">{title}</h1>
      </div>
      {action}
    </div>
  )
}

function StatusBadge({ status }: { status: string }): ReactElement {
  return <Badge variant={statusBadgeVariant(status)}>{STATUS_LABELS_FR[status] ?? status}</Badge>
}

// =============================================================================
// Dashboard
// =============================================================================
export function ClientDashboard(): ReactElement {
  const { user } = useAuth()
  const [stats, setStats] = useState({ requests: 0, orders: 0, pending: 0 })
  const [recent, setRecent] = useState<Req[]>([])

  useEffect(() => {
    void Promise.all([
      api.get<{ data: Req[] }>('/requests'),
      api.get<{ data: Order[] }>('/orders'),
    ]).then(([r, o]) => {
      setRecent(r.data.data.slice(0, 5))
      setStats({
        requests: r.data.data.length,
        orders: o.data.data.length,
        pending: r.data.data.filter((x) => ['PENDING', 'QUOTED'].includes(x.status)).length,
      })
    })
  }, [])

  return (
    <div>
      <PageHeader
        eyebrow={`Bienvenue ${user?.name.split(' ')[0]}`}
        title="Votre conciergerie privée."
        action={
          <Button
            onClick={() => {
              location.assign('/client/requests/new')
            }}
          >
            <Heart size={16} /> Nouvelle demande
          </Button>
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        <StatCard
          label="Demandes"
          value={stats.requests}
          icon={<Inbox className="text-accent" />}
        />
        <StatCard
          label="En attente"
          value={stats.pending}
          icon={<Sparkles className="text-accent" />}
        />
        <StatCard
          label="Commandes"
          value={stats.orders}
          icon={<ShoppingBag className="text-accent" />}
        />
      </div>

      <div className="mt-10">
        <h2 className="font-display mb-4 text-2xl">Dernières demandes</h2>
        {recent.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              Aucune demande encore.{' '}
              <Link to="/client/requests/new" className="text-primary underline">
                Créer ma première
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {recent.map((r) => (
              <Link key={r.id} to={`/client/requests/${r.id}`} className="block">
                <Card className="transition-colors hover:border-accent">
                  <CardContent className="flex items-center justify-between py-4">
                    <div>
                      <div className="font-medium">{r.title}</div>
                      <div className="text-sm text-muted-foreground">{formatDate(r.createdAt)}</div>
                    </div>
                    <StatusBadge status={r.status} />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function StatCard({
  label,
  value,
  icon,
}: {
  label: string
  value: number
  icon: ReactElement
}): ReactElement {
  return (
    <Card>
      <CardContent className="flex items-center justify-between py-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="font-display mt-2 text-4xl text-primary">{value}</div>
        </div>
        {icon}
      </CardContent>
    </Card>
  )
}

// =============================================================================
// Catalog (services + products)
// =============================================================================
export function ClientCatalog(): ReactElement {
  const [services, setServices] = useState<Service[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [tab, setTab] = useState<'services' | 'products'>('services')

  useEffect(() => {
    void api.get<{ data: Service[] }>('/services').then((r) => {
      setServices(r.data.data)
    })
    void api.get<{ data: Product[] }>('/products').then((r) => {
      setProducts(r.data.data)
    })
  }, [])

  return (
    <div>
      <PageHeader eyebrow="Catalogue" title="Services & Produits signature" />
      <div className="mb-6 inline-flex rounded-md border border-border bg-card p-1">
        <button
          onClick={() => {
            setTab('services')
          }}
          className={`rounded px-4 py-1.5 text-sm ${tab === 'services' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
        >
          Services ({services.length})
        </button>
        <button
          onClick={() => {
            setTab('products')
          }}
          className={`rounded px-4 py-1.5 text-sm ${tab === 'products' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
        >
          Produits ({products.length})
        </button>
      </div>

      {tab === 'services' ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {services.map((s) => (
            <article
              key={s.id}
              className="group overflow-hidden rounded-lg border border-border bg-card"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={s.imageUrl}
                  alt={s.name}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <p className="text-xs uppercase tracking-widest text-accent">
                  {SERVICE_CATEGORY_LABELS[s.category]}
                </p>
                <h3 className="font-display mt-2 text-xl">{s.name}</h3>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{s.description}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    À partir de {formatPrice(s.basePrice, s.currency)}
                  </span>
                  <Link
                    to={`/client/requests/new?service=${s.id}`}
                    className="text-sm text-primary hover:underline"
                  >
                    Demander →
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {products.map((p) => (
            <article
              key={p.id}
              className="group overflow-hidden rounded-lg border border-border bg-card"
            >
              <div className="aspect-square overflow-hidden">
                <img
                  src={p.imageUrl}
                  alt={p.name}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-4">
                <p className="text-xs uppercase tracking-widest text-accent">
                  {PRODUCT_CATEGORY_LABELS[p.category]}
                </p>
                <h3 className="mt-1 text-sm font-medium">{p.name}</h3>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-sm font-medium text-primary">
                    {formatPrice(p.price, p.currency)}
                  </span>
                  <Link
                    to={`/client/requests/new?product=${p.id}`}
                    className="text-xs text-muted-foreground hover:text-primary"
                  >
                    Commander
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  )
}

// =============================================================================
// New request
// =============================================================================
export function ClientNewRequest(): ReactElement {
  const navigate = useNavigate()
  const [services, setServices] = useState<Service[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [form, setForm] = useState({
    title: '',
    description: '',
    location: '',
    guestCount: '',
    startDate: '',
    endDate: '',
    serviceIds: [] as string[],
    productIds: [] as string[],
  })
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const url = new URL(window.location.href)
    const presetService = url.searchParams.get('service')
    const presetProduct = url.searchParams.get('product')
    void api.get<{ data: Service[] }>('/services').then((r) => {
      setServices(r.data.data)
    })
    void api.get<{ data: Product[] }>('/products').then((r) => {
      setProducts(r.data.data)
    })
    if (presetService) setForm((f) => ({ ...f, serviceIds: [presetService] }))
    if (presetProduct) setForm((f) => ({ ...f, productIds: [presetProduct] }))
  }, [])

  function toggle(arr: string[], id: string): string[] {
    return arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const body: Record<string, unknown> = {
        title: form.title,
        description: form.description,
        ...(form.location ? { location: form.location } : {}),
        ...(form.guestCount ? { guestCount: Number(form.guestCount) } : {}),
        ...(form.startDate ? { startDate: form.startDate } : {}),
        ...(form.endDate ? { endDate: form.endDate } : {}),
        details: { serviceIds: form.serviceIds, productIds: form.productIds },
      }
      const { data } = await api.post<{ data: Req }>('/requests', body)
      navigate(`/client/requests/${data.data.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <PageHeader eyebrow="Conciergerie" title="Nouvelle demande" />
      <form onSubmit={(e) => void onSubmit(e)} className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Card>
          <CardContent className="space-y-5 py-6">
            <div className="space-y-1.5">
              <Label htmlFor="title">Titre de votre demande</Label>
              <Input
                id="title"
                required
                value={form.title}
                onChange={(e) => {
                  setForm({ ...form, title: e.target.value })
                }}
                placeholder="Ex. Dîner étoilé pour 8 personnes"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="description">Décrivez ce que vous souhaitez</Label>
              <Textarea
                id="description"
                required
                rows={6}
                value={form.description}
                onChange={(e) => {
                  setForm({ ...form, description: e.target.value })
                }}
                placeholder="Allergies, ambiance souhaitée, contraintes, références…"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="location">Lieu</Label>
                <Input
                  id="location"
                  value={form.location}
                  onChange={(e) => {
                    setForm({ ...form, location: e.target.value })
                  }}
                  placeholder="Port Hercule, Monaco"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="guestCount">Nombre d'invités</Label>
                <Input
                  id="guestCount"
                  type="number"
                  min={1}
                  value={form.guestCount}
                  onChange={(e) => {
                    setForm({ ...form, guestCount: e.target.value })
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="startDate">Date de début</Label>
                <Input
                  id="startDate"
                  type="datetime-local"
                  value={form.startDate}
                  onChange={(e) => {
                    setForm({ ...form, startDate: e.target.value })
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="endDate">Date de fin</Label>
                <Input
                  id="endDate"
                  type="datetime-local"
                  value={form.endDate}
                  onChange={(e) => {
                    setForm({ ...form, endDate: e.target.value })
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm uppercase tracking-widest text-muted-foreground">
                Services associés
              </CardTitle>
            </CardHeader>
            <CardContent className="max-h-72 space-y-1.5 overflow-auto py-2">
              {services.map((s) => (
                <label
                  key={s.id}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-muted"
                >
                  <input
                    type="checkbox"
                    checked={form.serviceIds.includes(s.id)}
                    onChange={() => {
                      setForm({ ...form, serviceIds: toggle(form.serviceIds, s.id) })
                    }}
                  />
                  <span className="text-sm">{s.name}</span>
                </label>
              ))}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm uppercase tracking-widest text-muted-foreground">
                Produits associés
              </CardTitle>
            </CardHeader>
            <CardContent className="max-h-72 space-y-1.5 overflow-auto py-2">
              {products.map((p) => (
                <label
                  key={p.id}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-muted"
                >
                  <input
                    type="checkbox"
                    checked={form.productIds.includes(p.id)}
                    onChange={() => {
                      setForm({ ...form, productIds: toggle(form.productIds, p.id) })
                    }}
                  />
                  <span className="text-sm">
                    {p.name} <span className="text-muted-foreground">— {formatPrice(p.price)}</span>
                  </span>
                </label>
              ))}
            </CardContent>
          </Card>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" isLoading={loading} disabled={loading}>
            {loading ? 'Envoi…' : 'Envoyer ma demande'}
          </Button>
        </div>
      </form>
    </div>
  )
}

// =============================================================================
// My requests
// =============================================================================
export function ClientRequests(): ReactElement {
  const [items, setItems] = useState<Req[]>([])

  useEffect(() => {
    void api.get<{ data: Req[] }>('/requests').then((r) => {
      setItems(r.data.data)
    })
  }, [])

  return (
    <div>
      <PageHeader
        eyebrow="Mes échanges"
        title="Mes demandes"
        action={
          <Button
            onClick={() => {
              location.assign('/client/requests/new')
            }}
          >
            Nouvelle demande
          </Button>
        }
      />
      {items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Aucune demande pour le moment.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <table className="w-full">
            <thead className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-6 py-3">Titre</th>
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3">Statut</th>
                <th className="px-6 py-3">Devis</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-border/50 last:border-0 hover:bg-muted/40"
                >
                  <td className="px-6 py-4">
                    <Link to={`/client/requests/${r.id}`} className="font-medium hover:text-accent">
                      {r.title}
                    </Link>
                    {r.location && (
                      <div className="text-xs text-muted-foreground">{r.location}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {formatDate(r.createdAt)}
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="px-6 py-4 text-sm">{r.quotes?.length ?? 0}</td>
                  <td className="px-6 py-4 text-right">
                    <Link
                      to={`/client/requests/${r.id}`}
                      className="text-sm text-primary hover:underline"
                    >
                      Voir →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  )
}

// =============================================================================
// Request detail (with quote + accept + pay)
// =============================================================================
export function ClientRequestDetail(): ReactElement {
  const { id } = useParams<{ id: string }>()
  const [req, setReq] = useState<(Req & { quotes?: Quote[] }) | null>(null)
  const [busy, setBusy] = useState(false)
  const navigate = useNavigate()

  function reload() {
    if (!id) return
    void api.get<{ data: Req }>(`/requests/${id}`).then((r) => {
      setReq(r.data.data)
    })
  }
  useEffect(reload, [id])

  async function acceptQuote(quoteId: string) {
    setBusy(true)
    try {
      const { data } = await api.post<{ data: Order }>(`/quotes/${quoteId}/accept`)
      navigate(`/client/orders/${data.data.id}`)
    } finally {
      setBusy(false)
    }
  }

  async function rejectQuote(quoteId: string) {
    setBusy(true)
    try {
      await api.post(`/quotes/${quoteId}/reject`)
      reload()
    } finally {
      setBusy(false)
    }
  }

  if (!req) return <div className="text-muted-foreground">Chargement…</div>

  return (
    <div>
      <PageHeader
        eyebrow={STATUS_LABELS_FR[req.status]}
        title={req.title}
        action={<StatusBadge status={req.status} />}
      />
      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Card>
          <CardContent className="space-y-4 py-6">
            <h3 className="font-medium">Détails de la demande</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{req.description}</p>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              {req.location && <Info label="Lieu" value={req.location} />}
              {req.guestCount && <Info label="Invités" value={String(req.guestCount)} />}
              <Info label="Début" value={formatDate(req.startDate)} />
              <Info label="Fin" value={formatDate(req.endDate)} />
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-widest text-muted-foreground">
              Devis
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 py-2">
            {!req.quotes || req.quotes.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Aucun devis pour le moment. L'équipe MyPivot vous répond sous 24 h.
              </p>
            ) : (
              req.quotes.map((q) => (
                <div key={q.id} className="space-y-2 rounded-md border border-border p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{formatPrice(q.total, q.currency)}</span>
                    <StatusBadge status={q.status} />
                  </div>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    {q.items?.map((it) => (
                      <div key={it.id} className="flex justify-between">
                        <span>
                          {it.label} × {it.quantity}
                        </span>
                        <span>{formatPrice(it.total, q.currency)}</span>
                      </div>
                    ))}
                  </div>
                  {q.status === 'SENT' && (
                    <div className="flex gap-2 pt-2">
                      <Button size="sm" disabled={busy} onClick={() => void acceptQuote(q.id)}>
                        Accepter & Payer
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => void rejectQuote(q.id)}
                      >
                        Refuser
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function Info({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div>
      <dt className="text-xs uppercase tracking-widest text-muted-foreground">{label}</dt>
      <dd className="mt-1">{value}</dd>
    </div>
  )
}

// =============================================================================
// My orders + Order detail + pay + review
// =============================================================================
export function ClientOrders(): ReactElement {
  const [items, setItems] = useState<Order[]>([])
  useEffect(() => {
    void api.get<{ data: Order[] }>('/orders').then((r) => {
      setItems(r.data.data)
    })
  }, [])

  return (
    <div>
      <PageHeader eyebrow="Provisioning" title="Mes commandes" />
      {items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Aucune commande pour le moment.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((o) => (
            <Link key={o.id} to={`/client/orders/${o.id}`}>
              <Card className="transition-colors hover:border-accent">
                <CardContent className="flex items-center justify-between py-4">
                  <div>
                    <div className="font-medium">{o.request?.title}</div>
                    <div className="text-sm text-muted-foreground">
                      {formatDate(o.createdAt)} — {formatPrice(o.amount, o.currency)}
                    </div>
                  </div>
                  <StatusBadge status={o.status} />
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

export function ClientOrderDetail(): ReactElement {
  const { id } = useParams<{ id: string }>()
  const [order, setOrder] = useState<Order | null>(null)
  const [payment, setPayment] = useState<Payment | null>(null)
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' })
  const [submitting, setSubmitting] = useState(false)

  function reload() {
    if (!id) return
    void api.get<{ data: Order }>(`/orders/${id}`).then((r) => {
      setOrder(r.data.data)
    })
  }
  useEffect(reload, [id])

  async function startPayment() {
    if (!order) return
    setSubmitting(true)
    try {
      const { data } = await api.post<{ data: { payment: Payment; portalUrl: string } }>(
        '/payments/init',
        { orderId: order.id },
      )
      setPayment(data.data.payment)
      // Open FAM portal in a new tab (in real deploy this is the hosted page)
      window.open(data.data.portalUrl, '_blank', 'noopener')
    } finally {
      setSubmitting(false)
    }
  }

  async function devConfirm() {
    if (!payment) return
    setSubmitting(true)
    try {
      await api.post('/payments/dev/confirm', { paymentId: payment.id })
      reload()
      setPayment(null)
    } finally {
      setSubmitting(false)
    }
  }

  async function submitReview() {
    if (!order) return
    setSubmitting(true)
    try {
      await api.post('/reviews', {
        orderId: order.id,
        rating: reviewForm.rating,
        comment: reviewForm.comment,
      })
      reload()
    } finally {
      setSubmitting(false)
    }
  }

  if (!order) return <div className="text-muted-foreground">Chargement…</div>

  return (
    <div>
      <PageHeader
        eyebrow={STATUS_LABELS_FR[order.status]}
        title={order.request?.title ?? 'Commande'}
        action={<StatusBadge status={order.status} />}
      />

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Card>
          <CardContent className="space-y-3 py-6">
            <h3 className="font-medium">Détail du devis accepté</h3>
            {order.quote?.items?.map((it) => (
              <div
                key={it.id}
                className="flex items-center justify-between border-b border-border/50 py-2 text-sm last:border-0"
              >
                <div>
                  <div>
                    {it.label} <span className="text-muted-foreground">× {it.quantity}</span>
                  </div>
                  {it.description && (
                    <div className="text-xs text-muted-foreground">{it.description}</div>
                  )}
                </div>
                <div>{formatPrice(it.total, order.currency)}</div>
              </div>
            ))}
            <div className="flex justify-between border-t border-border pt-3 font-display text-xl">
              <span>Total</span>
              <span>{formatPrice(order.amount, order.currency)}</span>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {order.status === 'PENDING_PAYMENT' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-widest text-muted-foreground">
                  Paiement
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 py-2">
                <p className="text-sm text-muted-foreground">
                  Réglez votre commande via le portail sécurisé FAM (Mangopay).
                </p>
                {!payment ? (
                  <Button
                    className="w-full"
                    onClick={() => void startPayment()}
                    disabled={submitting}
                  >
                    Payer {formatPrice(order.amount, order.currency)}
                  </Button>
                ) : (
                  <div className="space-y-2 rounded border border-border p-3 text-sm">
                    <p className="text-muted-foreground">
                      Portail FAM ouvert dans un nouvel onglet.
                    </p>
                    <a
                      href={payment.famPortalUrl ?? '#'}
                      target="_blank"
                      rel="noopener"
                      className="text-primary underline"
                    >
                      Ré-ouvrir le portail
                    </a>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={() => void devConfirm()}
                      disabled={submitting}
                    >
                      Simuler le paiement (dev)
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {['DELIVERED', 'COMPLETED'].includes(order.status) && !order.review && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-widest text-muted-foreground">
                  Votre avis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 py-2">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      onClick={() => {
                        setReviewForm({ ...reviewForm, rating: n })
                      }}
                      className={n <= reviewForm.rating ? 'text-accent' : 'text-muted-foreground'}
                    >
                      ★
                    </button>
                  ))}
                </div>
                <Textarea
                  rows={4}
                  value={reviewForm.comment}
                  onChange={(e) => {
                    setReviewForm({ ...reviewForm, comment: e.target.value })
                  }}
                  placeholder="Partagez votre expérience…"
                />
                <Button
                  className="w-full"
                  onClick={() => void submitReview()}
                  disabled={submitting}
                >
                  Publier mon avis
                </Button>
              </CardContent>
            </Card>
          )}

          {order.review && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm uppercase tracking-widest text-muted-foreground">
                  Avis publié
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 py-2">
                <div className="text-accent">
                  {'★'.repeat(order.review.rating)}
                  {'☆'.repeat(5 - order.review.rating)}
                </div>
                <p className="text-sm">{order.review.comment}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// Profile
// =============================================================================
export function ClientProfile(): ReactElement {
  const { user } = useAuth()
  if (!user) return <></>
  return (
    <div>
      <PageHeader eyebrow="Compte" title="Mon profil" />
      <Card className="max-w-2xl">
        <CardContent className="space-y-3 py-6">
          <Field label="Nom" value={user.name} />
          <Field label="Email" value={user.email} />
          <Field label="Téléphone" value={user.phone ?? '—'} />
          <Field label="Rôle" value={user.role} />
        </CardContent>
      </Card>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div className="flex items-baseline justify-between border-b border-border/50 py-2 last:border-0">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  )
}

// Re-export typed exports
export type { Service, Product, Req as Request, Quote, Order, Payment }
// Tab select reused
export { Select }
